import {
  AggregatedAlertsPaginatedResponse,
  AlertHistoryResponse,
  AlertPaginatedResponse,
  AlertsApiGetAggregatedAlertsRequest,
  AlertsApiGetAlertHistoryRequest,
  AlertsApiGetAlertsRequest,
  AlertsApiGetAlertTimelineRequest,
  AlertsApiGetRuleIdSummaryRequest,
  AlertTimelineResponse,
  RuleIdSummaryResponse,
} from '@ariksa/notification/api';

import {
  AlertVaultAlertService,
  isAlertVaultEnabled,
} from 'api/alert-vault/client';
import { NotificationService } from 'api/services';
import { useAriksaQuery } from 'utils/query/useAriksaQuery';

export function useAlertService() {
  const alerts = useAriksaQuery<
    AlertsApiGetAlertsRequest,
    AlertPaginatedResponse
  >({
    queryKey: [],
    fetcher: req => {
      return (isAlertVaultEnabled()
        ? AlertVaultAlertService.list({
            accountId: req.accountId ?? undefined,
            environmentId: req.environmentId ?? undefined,
            category: req.alertCategory ?? undefined,
            resourceType: req.resourceTypes?.[0] ?? undefined,
            severity: req.severities ?? undefined,
            search: req.searchString ?? undefined,
            pageNumber: req.pageNumber ?? undefined,
            pageSize: req.pageSize ?? undefined,
          })
        : NotificationService.Alerts.getAlerts(req)
      ).then(d => d.data);
    },
  });

  const alertTimeline = useAriksaQuery<
    AlertsApiGetAlertTimelineRequest,
    AlertTimelineResponse[]
  >({
    queryKey: [],
    fetcher: req => {
      return (isAlertVaultEnabled()
        ? AlertVaultAlertService.activity(req.alertId)
        : NotificationService.Alerts.getAlertTimeline(req)
      ).then(d => d.data);
    },
  });

  const alertHistory = useAriksaQuery<
    AlertsApiGetAlertHistoryRequest,
    AlertHistoryResponse
  >({
    queryKey: [],
    fetcher: req => {
      return (isAlertVaultEnabled()
        ? AlertVaultAlertService.history(req)
        : NotificationService.Alerts.getAlertHistory(req)
      ).then(d => d.data);
    },
  });

  const aggregatedAlerts = useAriksaQuery<
    AlertsApiGetAggregatedAlertsRequest,
    AggregatedAlertsPaginatedResponse
  >({
    queryKey: [],
    fetcher: req => {
      return (isAlertVaultEnabled()
        ? AlertVaultAlertService.aggregates({
            accountId: req.accountId ?? undefined,
            environmentId: req.environmentId ?? undefined,
            resourceType: req.resourceTypes?.[0] ?? undefined,
            category: req.alertCategory ?? undefined,
            severity: req.severities ?? undefined,
            search: req.search ?? undefined,
            from: req.startDate ?? undefined,
            to: req.endDate ?? undefined,
            groupBy: 'resource',
            pageNumber: req.pageNumber ?? undefined,
            pageSize: req.pageSize ?? undefined,
          })
        : NotificationService.Alerts.getAggregatedAlerts(req)
      ).then(d => d.data);
    },
  });

  const alertsByRuleIdSummary = useAriksaQuery<
    AlertsApiGetRuleIdSummaryRequest,
    RuleIdSummaryResponse[]
  >({
    queryKey: [],
    fetcher: req => {
      return (isAlertVaultEnabled()
        ? AlertVaultAlertService.ruleSummaries(req.ruleIdSummaryRequest)
        : NotificationService.Alerts.getRuleIdSummary(req)
      ).then(d => d.data);
    },
  });

  return {
    alertTimeline,
    alertHistory,
    aggregatedAlerts,
    alerts,
    alertsByRuleIdSummary,
  };
}
